// THROWAWAY DEV TOOL — Responsive Lab (mobile-optimized branch).
//
// Renders the SAME app route in N iframes, each at its own breakpoint
// width, and MIRRORS your interactions across all of them — so you walk
// a flow (e.g. add-brand onboarding) ONCE and every breakpoint keeps up.
//
// PLUS an annotation layer: turn on "Draw", scribble arrows / boxes / notes
// over the panes, then screenshot (macOS: ⌘⇧⌃4 copies to clipboard) and
// paste to Claude. A marked-up screenshot is the fastest way to spec a pile
// of small UI tweaks in one shot.
//
// How the sync works
// ------------------
//  - Each pane is an <iframe> because an iframe is the only thing that
//    gives a pane its own real viewport width, which is what Tailwind's
//    sm:/md: MEDIA queries respond to. (CSS transform:scale just zooms
//    the desktop layout — it does NOT trigger the mobile one.)
//  - The panes are same-origin, so this parent page reads each iframe's
//    document and replays events into the others. Auth is shared via
//    localStorage, so every pane is signed in if you are.
//
// The one honest caveat
// ---------------------
//  Each pane runs its OWN JavaScript, so a mirrored "Get Started" fires N
//  independent backend calls (N× cost). For LAYOUT testing that's exactly
//  right. File uploads can't be mirrored (browser security).
//
// To delete when done: remove this file + its /responsive-lab route.

import { useCallback, useEffect, useRef, useState } from 'react';

type Frame = { id: string; label: string; width: number; height: number };

const LS_KEY = 'responsive-lab-config-v1';

const DEFAULT_FRAMES: Frame[] = [
  { id: 'm', label: 'Mobile', width: 390, height: 844 },
  { id: 't', label: 'Tablet', width: 768, height: 1024 },
  { id: 'l', label: 'Laptop', width: 1280, height: 800 },
  { id: 'd', label: 'Desktop', width: 1536, height: 864 },
];

const PRESETS: [string, number, number][] = [
  ['iPhone SE', 375, 667],
  ['iPhone 14', 390, 844],
  ['Pixel', 412, 915],
  ['iPad', 768, 1024],
  ['iPad Pro', 1024, 1366],
  ['Laptop', 1280, 800],
  ['Desktop', 1536, 864],
  ['Wide', 1920, 1080],
];

const QUICK_ROUTES = [
  '/brands/new',
  '/create',
  '/appv3/product-shot',
  '/gallery',
  '/ad-strategist-v1',
  '/brand',
  '/products',
];

const PEN_COLORS = ['#ff2d55', '#0a84ff', '#30d158', '#ffd60a', '#ffffff', '#000000'];

type SavedConfig = { url: string; frames: Frame[]; scale: number; sync: boolean };

function loadConfig(): SavedConfig {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<SavedConfig>;
      return {
        url: typeof p.url === 'string' ? p.url : '/brands/new',
        frames: Array.isArray(p.frames) && p.frames.length ? p.frames : DEFAULT_FRAMES,
        scale: typeof p.scale === 'number' ? p.scale : 0.55,
        sync: p.sync !== false,
      };
    }
  } catch {
    /* ignore */
  }
  return { url: '/brands/new', frames: DEFAULT_FRAMES, scale: 0.55, sync: true };
}

// ── annotation shapes ──────────────────────────────────────────────────
type Tool = 'pen' | 'arrow' | 'box' | 'text';
type Shape =
  | { t: 'pen'; color: string; pts: [number, number][] }
  | { t: 'arrow'; color: string; x1: number; y1: number; x2: number; y2: number }
  | { t: 'box'; color: string; x1: number; y1: number; x2: number; y2: number }
  | { t: 'text'; color: string; x: number; y: number; text: string };

// nth-of-type CSS path from <body>. Robust across breakpoints because the
// app's responsive changes are className-only (identical DOM structure).
function domPath(el: Element, body: Element): string {
  const segs: string[] = [];
  let node: Element | null = el;
  while (node && node !== body && node.parentElement) {
    const tag = node.tagName;
    let idx = 1;
    for (let sib = node.previousElementSibling; sib; sib = sib.previousElementSibling) {
      if (sib.tagName === tag) idx++;
    }
    segs.unshift(`${tag.toLowerCase()}:nth-of-type(${idx})`);
    node = node.parentElement;
  }
  return segs.join(' > ');
}

function queryPath(doc: Document, path: string): Element | null {
  if (!path) return null;
  try {
    return doc.body.querySelector(':scope > ' + path);
  } catch {
    return null;
  }
}

export default function ResponsiveLabPage() {
  const init = loadConfig();
  const [url, setUrl] = useState(init.url);
  const [urlDraft, setUrlDraft] = useState(init.url);
  const [frames, setFrames] = useState<Frame[]>(init.frames);
  const [scale, setScale] = useState(init.scale);
  const [sync, setSync] = useState(init.sync);
  const [nonce, setNonce] = useState(0); // bump to force-reload all panes
  const [leader, setLeader] = useState<string | null>(null);

  // annotation
  const [draw, setDraw] = useState(false);
  const [tool, setTool] = useState<Tool>('arrow');
  const [penColor, setPenColor] = useState(PEN_COLORS[0]);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const activeShape = useRef<Shape | null>(null);

  const iframeMap = useRef<Map<string, HTMLIFrameElement>>(new Map());
  const syncRef = useRef(sync);
  const replaying = useRef(false);
  syncRef.current = sync;

  // Persist config.
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ url, frames, scale, sync }));
    } catch {
      /* ignore */
    }
  }, [url, frames, scale, sync]);

  // ── canvas draw ──────────────────────────────────────────────────────
  const paint = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cv.width, cv.height);
    const all = activeShape.current ? [...shapes, activeShape.current] : shapes;
    for (const s of all) {
      ctx.strokeStyle = s.color;
      ctx.fillStyle = s.color;
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      if (s.t === 'pen') {
        ctx.beginPath();
        s.pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
        ctx.stroke();
      } else if (s.t === 'box') {
        ctx.strokeRect(Math.min(s.x1, s.x2), Math.min(s.y1, s.y2), Math.abs(s.x2 - s.x1), Math.abs(s.y2 - s.y1));
      } else if (s.t === 'arrow') {
        ctx.beginPath();
        ctx.moveTo(s.x1, s.y1);
        ctx.lineTo(s.x2, s.y2);
        ctx.stroke();
        const a = Math.atan2(s.y2 - s.y1, s.x2 - s.x1);
        const h = 14;
        ctx.beginPath();
        ctx.moveTo(s.x2, s.y2);
        ctx.lineTo(s.x2 - h * Math.cos(a - Math.PI / 7), s.y2 - h * Math.sin(a - Math.PI / 7));
        ctx.lineTo(s.x2 - h * Math.cos(a + Math.PI / 7), s.y2 - h * Math.sin(a + Math.PI / 7));
        ctx.closePath();
        ctx.fill();
      } else if (s.t === 'text') {
        ctx.font = 'bold 18px ui-sans-serif, system-ui, sans-serif';
        const w = ctx.measureText(s.text).width;
        ctx.fillStyle = 'rgba(0,0,0,0.65)';
        ctx.fillRect(s.x - 4, s.y - 18, w + 8, 24);
        ctx.fillStyle = s.color;
        ctx.fillText(s.text, s.x, s.y);
      }
    }
  }, [shapes]);

  // Size the canvas to the viewport (crisp via dpr) + repaint on resize.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      cv.width = window.innerWidth * dpr;
      cv.height = window.innerHeight * dpr;
      cv.style.width = window.innerWidth + 'px';
      cv.style.height = window.innerHeight + 'px';
      paint();
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [paint]);

  useEffect(() => {
    paint();
  }, [shapes, paint]);

  const onCanvasDown = (e: React.MouseEvent) => {
    if (!draw) return;
    const x = e.clientX;
    const y = e.clientY;
    if (tool === 'text') {
      const text = window.prompt('Note:');
      if (text) setShapes((s) => [...s, { t: 'text', color: penColor, x, y, text }]);
      return;
    }
    if (tool === 'pen') activeShape.current = { t: 'pen', color: penColor, pts: [[x, y]] };
    else if (tool === 'arrow') activeShape.current = { t: 'arrow', color: penColor, x1: x, y1: y, x2: x, y2: y };
    else activeShape.current = { t: 'box', color: penColor, x1: x, y1: y, x2: x, y2: y };
    paint();
  };
  const onCanvasMove = (e: React.MouseEvent) => {
    const s = activeShape.current;
    if (!s) return;
    const x = e.clientX;
    const y = e.clientY;
    if (s.t === 'pen') s.pts.push([x, y]);
    else if (s.t === 'arrow' || s.t === 'box') {
      s.x2 = x;
      s.y2 = y;
    }
    paint();
  };
  const onCanvasUp = () => {
    const s = activeShape.current;
    if (s) setShapes((prev) => [...prev, s]);
    activeShape.current = null;
  };

  const replay = useCallback(
    (srcDoc: Document, srcEl: Element, kind: 'click' | 'input' | 'change' | 'key', value: string) => {
      const path = domPath(srcEl, srcDoc.body);
      replaying.current = true;
      try {
        for (const iframe of iframeMap.current.values()) {
          let doc: Document | null = null;
          let win: Window | null = null;
          try {
            doc = iframe.contentDocument;
            win = iframe.contentWindow;
          } catch {
            continue;
          }
          if (!doc || !win || doc === srcDoc) continue;
          const el = queryPath(doc, path);
          if (!el) continue;
          try {
            if (kind === 'click') {
              (el as HTMLElement).click();
            } else if (kind === 'input' || kind === 'change') {
              const w = win as unknown as Record<string, { prototype: object }>;
              const tag = el.tagName;
              const proto =
                tag === 'TEXTAREA'
                  ? w.HTMLTextAreaElement.prototype
                  : tag === 'SELECT'
                    ? w.HTMLSelectElement.prototype
                    : w.HTMLInputElement.prototype;
              const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
              const anyEl = el as unknown as { value: unknown; checked?: boolean };
              if ((el as HTMLInputElement).type === 'checkbox' || (el as HTMLInputElement).type === 'radio') {
                (el as HTMLInputElement).checked = value === 'true';
              } else if (setter) {
                setter.call(el, value);
              } else {
                anyEl.value = value;
              }
              el.dispatchEvent(new (win as unknown as { Event: typeof Event }).Event(kind, { bubbles: true }));
            } else if (kind === 'key') {
              el.dispatchEvent(
                new (win as unknown as { KeyboardEvent: typeof KeyboardEvent }).KeyboardEvent('keydown', {
                  key: value,
                  bubbles: true,
                }),
              );
            }
          } catch {
            /* one pane failing shouldn't stop the others */
          }
        }
      } finally {
        replaying.current = false;
      }
    },
    [],
  );

  // Attach capture-phase listeners to a freshly-loaded iframe document.
  const attach = useCallback(
    (frameId: string, iframe: HTMLIFrameElement) => {
      let doc: Document | null = null;
      try {
        doc = iframe.contentDocument;
      } catch {
        return;
      }
      if (!doc) return;
      const marked = doc as unknown as { __rlAttached?: boolean };
      if (marked.__rlAttached) return;
      marked.__rlAttached = true;

      const lead = () => setLeader(frameId);

      doc.addEventListener(
        'click',
        (e) => {
          if (!syncRef.current || replaying.current) return;
          const t = e.target as Element | null;
          if (!t || !t.tagName) return;
          lead();
          replay(doc!, t, 'click', '');
        },
        true,
      );
      doc.addEventListener(
        'input',
        (e) => {
          if (!syncRef.current || replaying.current) return;
          const t = e.target as (HTMLInputElement & Element) | null;
          if (!t || !t.tagName) return;
          lead();
          replay(doc!, t, 'input', t.value ?? '');
        },
        true,
      );
      doc.addEventListener(
        'change',
        (e) => {
          if (!syncRef.current || replaying.current) return;
          const t = e.target as (HTMLInputElement & Element) | null;
          if (!t || !t.tagName) return;
          if (t.type === 'checkbox' || t.type === 'radio') {
            lead();
            replay(doc!, t, 'change', String(t.checked));
          } else if (t.tagName === 'SELECT') {
            lead();
            replay(doc!, t, 'change', t.value ?? '');
          }
        },
        true,
      );
      doc.addEventListener(
        'keydown',
        (e) => {
          if (!syncRef.current || replaying.current) return;
          if (e.key !== 'Enter' && e.key !== 'Escape') return;
          const t = e.target as Element | null;
          if (!t || !t.tagName) return;
          lead();
          replay(doc!, t, 'key', e.key);
        },
        true,
      );
    },
    [replay],
  );

  const setIframeRef = useCallback((frameId: string, el: HTMLIFrameElement | null) => {
    if (el) iframeMap.current.set(frameId, el);
    else iframeMap.current.delete(frameId);
  }, []);

  const addFrame = (label: string, width: number, height: number) => {
    setFrames((f) => [...f, { id: `${Date.now()}-${Math.round(width)}`, label, width, height }]);
  };
  const removeFrame = (id: string) => setFrames((f) => f.filter((x) => x.id !== id));
  const patchFrame = (id: string, patch: Partial<Frame>) =>
    setFrames((f) => f.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const loadUrl = () => setUrl(urlDraft.trim() || '/');

  return (
    <div className="h-screen w-screen flex flex-col bg-neutral-900 text-neutral-100 overflow-hidden">
      {/* ── Control bar ─────────────────────────────────────────── */}
      <div className="relative z-50 flex-shrink-0 border-b border-neutral-700 bg-neutral-950 px-4 py-2.5 flex flex-col gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-bold tracking-tight text-pink-400">Responsive Lab</span>

          <div className="flex items-center gap-1">
            <input
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadUrl()}
              placeholder="/route to load"
              className="w-64 px-2 py-1 rounded bg-neutral-800 border border-neutral-700 text-sm font-mono focus:outline-none focus:border-pink-500"
            />
            <button
              onClick={loadUrl}
              className="px-3 py-1 rounded bg-pink-600 hover:bg-pink-500 text-sm font-semibold"
            >
              Load
            </button>
            <button
              onClick={() => setNonce((n) => n + 1)}
              title="Force-reload every pane (fresh load = back to step 1)"
              className="px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-sm"
            >
              ↻ Reload all
            </button>
          </div>

          <label className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={sync} onChange={(e) => setSync(e.target.checked)} />
            <span className={sync ? 'text-emerald-400 font-semibold' : 'text-neutral-400'}>
              Sync {sync ? 'ON' : 'off'}
            </span>
          </label>

          <label className="flex items-center gap-2 text-sm">
            Scale
            <input
              type="range"
              min={30}
              max={100}
              value={Math.round(scale * 100)}
              onChange={(e) => setScale(Number(e.target.value) / 100)}
              className="w-28 accent-pink-500"
            />
            <span className="w-9 tabular-nums text-neutral-400">{Math.round(scale * 100)}%</span>
          </label>

          <span className="text-xs text-amber-400/90 ml-auto">
            ⚠ each pane makes its own API calls ({frames.length}× cost) · file uploads don&apos;t mirror
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-neutral-500">routes:</span>
          {QUICK_ROUTES.map((r) => (
            <button
              key={r}
              onClick={() => {
                setUrlDraft(r);
                setUrl(r);
              }}
              className={`px-2 py-0.5 rounded text-xs font-mono border ${
                url === r
                  ? 'bg-pink-600 border-pink-500'
                  : 'bg-neutral-800 border-neutral-700 hover:bg-neutral-700'
              }`}
            >
              {r}
            </button>
          ))}
          <span className="text-xs text-neutral-500 ml-3">add pane:</span>
          {PRESETS.map(([label, w, h]) => (
            <button
              key={label}
              onClick={() => addFrame(label, w, h)}
              className="px-2 py-0.5 rounded text-xs bg-neutral-800 border border-neutral-700 hover:bg-neutral-700"
            >
              + {label} <span className="text-neutral-500">{w}</span>
            </button>
          ))}
        </div>

        {/* ── Annotation toolbar ── */}
        <div className="flex items-center gap-2 flex-wrap border-t border-neutral-800 pt-2">
          <button
            onClick={() => setDraw((d) => !d)}
            className={`px-3 py-0.5 rounded text-xs font-bold border ${
              draw ? 'bg-pink-600 border-pink-500 text-white' : 'bg-neutral-800 border-neutral-700 hover:bg-neutral-700'
            }`}
          >
            ✏️ Draw {draw ? 'ON' : 'off'}
          </button>
          {(['arrow', 'box', 'pen', 'text'] as Tool[]).map((tl) => (
            <button
              key={tl}
              onClick={() => {
                setTool(tl);
                if (!draw) setDraw(true);
              }}
              className={`px-2 py-0.5 rounded text-xs capitalize border ${
                draw && tool === tl ? 'bg-neutral-700 border-neutral-500' : 'bg-neutral-900 border-neutral-800 hover:bg-neutral-800'
              }`}
            >
              {tl}
            </button>
          ))}
          <span className="flex items-center gap-1 ml-1">
            {PEN_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => {
                  setPenColor(c);
                  if (!draw) setDraw(true);
                }}
                className={`w-5 h-5 rounded-full border-2 ${penColor === c ? 'border-white' : 'border-neutral-700'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </span>
          <button
            onClick={() => setShapes((s) => s.slice(0, -1))}
            className="px-2 py-0.5 rounded text-xs bg-neutral-800 border border-neutral-700 hover:bg-neutral-700"
          >
            Undo
          </button>
          <button
            onClick={() => setShapes([])}
            className="px-2 py-0.5 rounded text-xs bg-neutral-800 border border-neutral-700 hover:bg-neutral-700"
          >
            Clear
          </button>
          <span className="text-xs text-neutral-500 ml-2">
            {draw
              ? 'drawing — panes are frozen. Screenshot: ⌘⇧⌃4 (copies to clipboard) → paste to Claude.'
              : 'marks stay visible while off, so you can interact then screenshot.'}
          </span>
        </div>
      </div>

      {/* ── Panes ───────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-4">
        <div className="flex gap-4 items-start" style={{ width: 'max-content' }}>
          {frames.map((f) => (
            <div key={f.id} className="flex-shrink-0">
              <div
                className={`flex items-center justify-between mb-1.5 rounded-t px-2 py-1 text-xs ${
                  leader === f.id ? 'bg-pink-600' : 'bg-neutral-800'
                }`}
                style={{ width: f.width * scale }}
              >
                <input
                  value={f.label}
                  onChange={(e) => patchFrame(f.id, { label: e.target.value })}
                  className="bg-transparent font-semibold w-20 focus:outline-none"
                />
                <span className="flex items-center gap-1 text-neutral-300">
                  <input
                    type="number"
                    value={f.width}
                    onChange={(e) => patchFrame(f.id, { width: Math.max(200, Number(e.target.value) || 200) })}
                    className="w-14 bg-neutral-900/60 rounded px-1 text-right tabular-nums focus:outline-none"
                  />
                  ×
                  <input
                    type="number"
                    value={f.height}
                    onChange={(e) => patchFrame(f.id, { height: Math.max(300, Number(e.target.value) || 300) })}
                    className="w-14 bg-neutral-900/60 rounded px-1 text-right tabular-nums focus:outline-none"
                  />
                  <button
                    onClick={() => removeFrame(f.id)}
                    className="ml-1 w-5 h-5 rounded hover:bg-red-500/80 flex items-center justify-center"
                    title="Remove pane"
                  >
                    ✕
                  </button>
                </span>
              </div>
              <div
                className="bg-white rounded-b overflow-hidden ring-1 ring-neutral-700"
                style={{ width: f.width * scale, height: f.height * scale }}
              >
                <iframe
                  key={`${f.id}:${url}:${nonce}`}
                  ref={(el) => setIframeRef(f.id, el)}
                  src={url}
                  title={f.label}
                  onLoad={(e) => attach(f.id, e.currentTarget)}
                  style={{
                    width: f.width,
                    height: f.height,
                    border: 0,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Annotation canvas (fixed over the viewport, below the toolbar) ── */}
      <canvas
        ref={canvasRef}
        onMouseDown={onCanvasDown}
        onMouseMove={onCanvasMove}
        onMouseUp={onCanvasUp}
        onMouseLeave={onCanvasUp}
        className="fixed inset-0 z-40"
        style={{ pointerEvents: draw ? 'auto' : 'none', cursor: draw ? 'crosshair' : 'default' }}
      />
    </div>
  );
}
